import React from "react";
import { ArrowRight, ClipboardList, Stethoscope, Sparkles, Brain, HeartHandshake, Users } from "lucide-react";
import { ScrollAnimation } from "../ScrollAnimation";

const approaches = [
  {
    icon: ClipboardList,
    title: "Avaliação clínica detalhada",
    description: "Escuta da queixa atual, dos fatores desencadeantes e mapeamento dos eventos da história de vida e da história familiar.",
  },
  {
    icon: Stethoscope,
    title: "Diagnóstico e plano de tratamento",
    description: "Identificação precisa da natureza do seu sofrimento — cruzando sua história com a saúde clínica e exames — para traçar um caminho terapêutico sob medida.",
  },
  {
    icon: Sparkles,
    title: "Abordagem terapêutica integrada",
    description: "Prescrição medicamentosa criteriosa aliada a orientações sobre suplementação, exercícios e os autocuidados necessários para melhores resultados.",
  },
  {
    icon: Brain,
    title: "Psicoterapias integradas",
    description: "Terapia Cognitivo-Comportamental (TCC), Psicoterapia Dinâmica e Psicoterapia Sistêmica.",
  },
  {
    icon: HeartHandshake,
    title: "Psicoterapias inovadoras para o tratamento de traumas emocionais",
    description: "EMDR, Brainspotting e Psicoterapia Assistida por Cetamina.",
  },
  {
    icon: Users,
    title: "Acompanhamento e suporte familiar",
    description: "Espaço de apoio e psicoeducação para orientar a família e otimizar os resultados do tratamento, quando indicado.",
  },
];

export default function Approach() {
  return (
    <section id="abordagem" className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#faf9f7]">
      <div className="max-w-5xl mx-auto">
        <ScrollAnimation animation="fade-up">
          <span className="font-sans text-sm font-semibold text-[#C2A44A] tracking-wider uppercase mb-3 block">Abordagem</span>
        </ScrollAnimation>
        <ScrollAnimation animation="fade-up" delay={100}>
          <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#204613] mb-8 text-balance leading-tight">
            O ser humano se transforma quando é acolhido e compreendido - não apenas medicado.
          </h2>
        </ScrollAnimation>
        <ScrollAnimation animation="fade-up" delay={200}>
          <p className="font-serif text-base sm:text-lg leading-relaxed text-[#4a4a4a] mb-8 max-w-3xl">
            Nossa abordagem une o que há de mais avançado na psiquiatria clínica, neurociência e psicoterapia, com foco no trauma como eixo central do tratamento.
          </p>
        </ScrollAnimation>
        <ScrollAnimation animation="fade-up" delay={300}>
          <p className="font-sans text-lg sm:text-xl font-semibold text-[#698658] mb-8">Aqui, cada pessoa é acolhida em sua totalidade:</p>
        </ScrollAnimation>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12">
          {approaches.map((item, idx) => {
            const Icon = item.icon;
            return (
              <ScrollAnimation key={idx} animation="fade-up" delay={350 + idx * 100}>
                <div className="bg-white border border-[#ACC996]/25 rounded-2xl p-5 sm:p-6 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#698658]/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#698658]" />
                    </div>
                    <span className="font-sans text-2xl font-bold text-[#C2A44A]">{idx + 1}</span>
                  </div>
                  <h3 className="font-sans text-lg sm:text-xl font-bold text-[#204613] mb-2 leading-snug">{item.title}</h3>
                  <p className="font-serif text-sm sm:text-base text-[#3d3d3d] leading-relaxed">{item.description}</p>
                </div>
              </ScrollAnimation>
            );
          })}
        </div>

        <ScrollAnimation animation="fade-up" delay={600}>
          <div className="bg-[#ACC996]/15 border border-[#ACC996]/25 rounded-2xl p-6 sm:p-8 mb-10">
            <p className="font-serif text-base sm:text-lg text-[#4a4a4a] leading-relaxed text-center">
              Acreditamos que a verdadeira transformação acontece quando a escuta, a ciência e a sensibilidade se unem para promover{" "}
              <strong className="text-[#204613]">equilíbrio emocional e liberdade interior</strong>.
            </p>
          </div>
        </ScrollAnimation>

        <ScrollAnimation animation="scale" delay={700}>
          <div className="text-center">
            <a
              href="#tratamentos"
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-[#C2A44A] text-white rounded-xl font-sans font-semibold text-sm sm:text-base hover:bg-[#b3943f] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              Veja Nossos Tratamentos
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
}
